import React from 'react';
import { HoldGroupKey } from '../copy';
import { formatCount, percent, shortDay } from '../format';
import { DayCounts } from '../../../api/cafAutomation';
import { senderEmail } from '../lenders';
import { DailyPoint, GapRow, OutcomeCounts, ReasonRow } from './howData';

// Ryan's ruling: the outcome wording is "Done by AI" / "Archived by AI" /
// "Marked as manual" -- never "sent to staff" / "went to staff" anywhere in
// this file. Attached-but-left-open is folded into `staff` upstream (see
// howData.toOutcomes) and reads here as "Marked as manual".
const OUTCOME_LABELS = ['Done by AI', 'Archived by AI', 'Marked as manual'];

export const OutcomeDonut: React.FC<{ counts: OutcomeCounts }> = ({ counts }) => {
  const values = [counts.done, counts.archived, counts.staff];
  const total = values.reduce((sum, value) => sum + value, 0);
  // No zero-filled charts: a genuinely empty window says so plainly.
  if (!total) return <div className="cur-empty">No emails were decided in this period.</div>;
  const radius = 64;
  const circumference = 2 * Math.PI * radius;
  const gap = 2.5;
  let offset = 0;
  const handled = percent(counts.done + counts.archived, total);
  return (
    <div className="cur-donut-wrap">
      <svg viewBox="0 0 180 180" className="cur-donut" role="img" aria-label={`${handled}% of emails handled by the AI`}>
        {values.map((value, index) => {
          const length = (value / total) * circumference;
          const arc = (
            <circle key={OUTCOME_LABELS[index]} cx="90" cy="90" r={radius} className={`cur-seg-arc cur-seg-${index}`}
              strokeDasharray={`${Math.max(0, length - gap).toFixed(2)} ${(circumference - length + gap).toFixed(2)}`}
              strokeDashoffset={(-offset).toFixed(2)} transform="rotate(-90 90 90)">
              <title>{`${OUTCOME_LABELS[index]}: ${formatCount(value)} emails, ${percent(value, total)}%`}</title>
            </circle>
          );
          offset += length;
          return arc;
        })}
        <text x="90" y="92" textAnchor="middle" className="cur-donut-big">{handled}%</text>
        <text x="90" y="112" textAnchor="middle" className="cur-donut-small">handled by AI</text>
      </svg>
      <ul className="cur-legend">
        {values.map((value, index) => (
          <li key={OUTCOME_LABELS[index]}>
            <span className={`cur-sw cur-sw-${index}`} />
            <span>{OUTCOME_LABELS[index]}</span>
            <span className="cur-lg-val">{formatCount(value)}</span>
            <span className="cur-lg-pct">{percent(value, total)}%</span>
          </li>
        ))}
        <li className="cur-legend-total"><span /><span className="cur-muted">Total</span><span className="cur-lg-val">{formatCount(total)}</span><span /></li>
        <li className="cur-legend-note">Emails attached but left open count as marked as manual.</li>
      </ul>
    </div>
  );
};

interface KnownPoint { index: number; pct: number }

export const DailyLine: React.FC<{ points: DailyPoint[] }> = ({ points }) => {
  if (!points.length) return <div className="cur-empty">No emails were decided in this period.</div>;
  const known: KnownPoint[] = [];
  points.forEach((point, index) => { if (point.pct !== null) known.push({ index, pct: point.pct }); });
  // No zero-filled charts: a window that is nothing but gaps says so plainly,
  // rather than drawing a flat line through days with no recorded decisions.
  if (!known.length) return <div className="cur-empty">No emails were decided in this period.</div>;
  const width = 640;
  const height = 200;
  const left = 44;
  const right = 20;
  const top = 14;
  const bottom = 28;
  const innerWidth = width - left - right;
  const innerHeight = height - top - bottom;
  const low = Math.max(0, Math.floor((Math.min(...known.map((p) => p.pct)) - 10) / 10) * 10);
  const high = Math.min(100, Math.ceil((Math.max(...known.map((p) => p.pct)) + 10) / 10) * 10);
  const span = Math.max(10, high - low);
  const x = (index: number) => left + (points.length === 1 ? innerWidth / 2 : (index * innerWidth) / (points.length - 1));
  const y = (value: number) => top + innerHeight - ((value - low) / span) * innerHeight;

  // Break the line at every gap so a filled zero/gap day never silently joins
  // two distant days into one misleading slope.
  const segments: KnownPoint[][] = [];
  let current: KnownPoint[] = [];
  points.forEach((point, index) => {
    if (point.pct === null) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push({ index, pct: point.pct });
    }
  });
  if (current.length) segments.push(current);
  const linePath = (segment: KnownPoint[]) => segment
    .map((entry, i) => `${i ? 'L' : 'M'}${x(entry.index).toFixed(1)},${y(entry.pct).toFixed(1)}`)
    .join(' ');
  const areaPath = (segment: KnownPoint[]) => {
    const line = linePath(segment);
    const lastIndex = segment[segment.length - 1].index;
    const firstIndex = segment[0].index;
    return `${line} L${x(lastIndex).toFixed(1)},${y(low).toFixed(1)} L${x(firstIndex).toFixed(1)},${y(low).toFixed(1)} Z`;
  };

  const ticks = [0, 1, 2, 3].map((step) => low + Math.round((span * step) / 3));
  const first = points[0];
  const last = points[points.length - 1];
  const earliestKnown = known[0];
  const latestKnown = known[known.length - 1];
  const change = known.length > 1 ? latestKnown.pct - earliestKnown.pct : null;
  const labelIndexes = Array.from(new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]));
  return (
    <div>
      <div className="cur-kpi-line">
        <strong>{latestKnown.pct}%</strong><span>on {shortDay(points[latestKnown.index].date)}</span>
        {change !== null && (
          <span className={change >= 0 ? 'cur-up' : 'cur-down'}>
            {`${change >= 0 ? '▲' : '▼'} ${Math.abs(change)} ${Math.abs(change) === 1 ? 'point' : 'points'} since ${shortDay(points[earliestKnown.index].date)}`}
          </span>
        )}
      </div>
      <div className="cur-chart">
        <svg viewBox={`0 0 ${width} ${height}`} role="img"
          aria-label={`Share of emails handled by the AI each day, ${shortDay(first.date)} to ${shortDay(last.date)}`}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line className="cur-gridline" x1={left} x2={width - right} y1={y(tick)} y2={y(tick)} />
              <text className="cur-axis" x={left - 8} y={y(tick) + 4} textAnchor="end">{tick}%</text>
            </g>
          ))}
          {segments.map((segment) => (
            <path key={`area-${segment[0].index}`} d={areaPath(segment)} className="cur-series-area" />
          ))}
          {segments.map((segment) => (
            <path key={`line-${segment[0].index}`} d={linePath(segment)} className="cur-series-line" />
          ))}
          {points.map((point, index) => (point.pct === null ? (
            <circle key={point.date} cx={x(index)} cy={y(low)} r={3} className="cur-gap-marker">
              <title>{`No decisions recorded on ${shortDay(point.date)}`}</title>
            </circle>
          ) : (
            <circle key={point.date} cx={x(index)} cy={y(point.pct)} r={index === points.length - 1 ? 4 : 2.5} className="cur-end-dot">
              <title>{`${shortDay(point.date)}: ${point.pct}% handled by AI (${formatCount(point.handled)} of ${formatCount(point.total)} emails)`}</title>
            </circle>
          )))}
          {labelIndexes.map((index) => (
            <text key={points[index].date} className="cur-axis" x={x(index)} y={height - 6}
              textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}>{shortDay(points[index].date)}</text>
          ))}
        </svg>
      </div>
    </div>
  );
};

export interface ReasonExtraRow { label: string; count: number }

type DisplayRow = { kind: 'reason'; row: ReasonRow } | { kind: 'extra'; label: string; count: number };

/**
 * Controller note 2: attached-but-left-open has no hold_reason code (it's a
 * different effect entirely, folded into "marked as manual" upstream -- see
 * howData.toOutcomes), so it can't join `rows` as a real, filterable
 * ReasonRow. It renders as one plain, non-clickable row instead -- no button
 * role, no aria-pressed -- inserted after the hold-reason groups and before
 * "Other reasons" (or at the end, if there's no "Other reasons" row).
 */
export const ReasonBars: React.FC<{
  rows: ReasonRow[];
  extra?: ReasonExtraRow | null;
  selected: HoldGroupKey | null;
  onSelect: (key: HoldGroupKey | null) => void;
}> = ({ rows, extra, selected, onSelect }) => {
  const showExtra = Boolean(extra && extra.count > 0);
  const display: DisplayRow[] = rows.map((row): DisplayRow => ({ kind: 'reason', row }));
  if (showExtra && extra) {
    const otherIndex = display.findIndex((item) => item.kind === 'reason' && item.row.key === 'other');
    const entry: DisplayRow = { kind: 'extra', label: extra.label, count: extra.count };
    if (otherIndex === -1) display.push(entry); else display.splice(otherIndex, 0, entry);
  }
  if (!display.length) return <div className="cur-empty">No emails were marked as manual in this period.</div>;
  const width = 560;
  const rowHeight = 42;
  const barWidth = width - 64;
  const countOf = (item: DisplayRow) => (item.kind === 'reason' ? item.row.count : item.count);
  const labelOf = (item: DisplayRow) => (item.kind === 'reason' ? item.row.label : item.label);
  const max = Math.max(...display.map(countOf));
  const niceMax = Math.max(1, Math.ceil(max / 25) * 25);
  return (
    <div className={`cur-chart cur-bars${selected ? ' has-sel' : ''}`}>
      {/* Controller ruling: hold reasons are never shown as a share of held --
          the two are counted differently, so only the plain count is shown. */}
      <svg viewBox={`0 0 ${width} ${display.length * rowHeight - 4}`} aria-label="Why emails were marked as manual">
        {display.map((item, index) => {
          const top = index * rowHeight;
          const count = countOf(item);
          const label = labelOf(item);
          const length = Math.max(3, (count / niceMax) * barWidth);
          if (item.kind === 'extra') {
            return (
              <g key="attached-left-open" className="cur-bar-row cur-bar-row--static">
                <text x="0" y={top + 14} className="cur-bar-label">{label}</text>
                <rect x="0" y={top + 21} width={length.toFixed(1)} height="10" rx="4" className="cur-bar" />
                <text x={(length + 8).toFixed(1)} y={top + 30} className="cur-bar-val">{formatCount(count)}</text>
              </g>
            );
          }
          const row = item.row;
          const choose = () => onSelect(selected === row.key ? null : row.key);
          return (
            <g key={row.key} className={`cur-bar-row${selected === row.key ? ' is-on' : ''}`} role="button" tabIndex={0}
              aria-pressed={selected === row.key} aria-label={`${label}: ${formatCount(count)} emails`}
              onClick={choose}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(); } }}>
              <rect x="0" y={top} width={width} height={rowHeight - 4} rx="4" className="cur-bar-hit" />
              <text x="0" y={top + 14} className="cur-bar-label">{label}</text>
              <rect x="0" y={top + 21} width={length.toFixed(1)} height="10" rx="4" className="cur-bar" />
              <text x={(length + 8).toFixed(1)} y={top + 30} className="cur-bar-val">{formatCount(count)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export const GapList: React.FC<{ rows: GapRow[]; onLabelNow: (sender: string) => void }> = ({ rows, onLabelNow }) => {
  if (!rows.length) {
    return <div className="cur-empty">No senders to review in the latest dashboard check.</div>;
  }
  const max = Math.max(...rows.map((row) => row.count));
  return (
    <>
      <p className="cur-card-sub">Review these senders and their example coverage from the latest dashboard check.</p>
      <ol className="cur-gaps">
        {rows.map((row, index) => (
          <li key={row.sender}>
            <span className="cur-rank">{index + 1}</span>
            <div className="cur-gap-main">
              <strong className="cur-ellip">{row.lender ?? senderEmail(row.sender)}</strong>
              {row.lender && <span className="cur-muted cur-small cur-ellip" title={senderEmail(row.sender)}>{senderEmail(row.sender)}</span>}
              <span className="cur-muted cur-small">{row.reason === 'no_examples' ? 'No example exists yet'
                : row.reason === 'coverage_unknown' ? 'Example coverage needs refresh'
                : row.reason === 'approval_pending' ? `${formatCount(row.existingExamples ?? 0)} existing ${row.existingExamples === 1 ? 'example needs' : 'examples need'} approval review`
                : row.reason === 'missing_vector' ? 'Approved example needs a search vector'
                : row.reason === 'switched_off' ? 'Existing example is switched off'
                : 'Existing example needs review'}</span>
              <div className="cur-gap-bar">
                <svg viewBox="0 0 140 8" preserveAspectRatio="none" aria-hidden="true">
                  <rect x="0" y="0" width="140" height="8" rx="4" className="cur-track" />
                  <rect x="0" y="0" width={Math.max(6, (row.count / max) * 140)} height="8" rx="4" className="cur-bar" />
                </svg>
                <span className="cur-gap-n">{formatCount(row.count)} incoming emails</span>
              </div>
            </div>
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" aria-label={`Review examples from ${senderEmail(row.sender)}`}
              onClick={() => onLabelNow(row.sender)}>Review examples</button>
          </li>
        ))}
      </ol>
    </>
  );
};

export interface StackSeries { key: 'auto' | 'workflow' | 'manual'; label: string; tone: 0 | 1 | 2 }

const niceCeil = (value: number): number => {
  const power = 10 ** Math.floor(Math.log10(Math.max(1, value)));
  const step = [1, 2, 2.5, 5, 10].find((candidate) => candidate * power >= value) ?? 10;
  return step * power;
};

/** Automation > Is it working?: one stacked bar per day (spec §5.2). Hover a bar for its numbers. */
export const StackedDailyBars: React.FC<{ days: DayCounts[]; series: StackSeries[]; label: string }> = ({ days, series, label }) => {
  const totals = days.map((day) => series.reduce((sum, item) => sum + day[item.key], 0));
  const max = Math.max(0, ...totals);
  if (!max) return <div className="cur-empty">No lender emails were decided in this period.</div>;
  const width = 640;
  const height = 220;
  const left = 40;
  const right = 8;
  const top = 10;
  const bottom = 26;
  const innerWidth = width - left - right;
  const innerHeight = height - top - bottom;
  const scale = niceCeil(max);
  const slot = innerWidth / days.length;
  const barWidth = Math.max(4, Math.min(44, slot * 0.6));
  const y = (value: number) => top + innerHeight - (value / scale) * innerHeight;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => Math.round(scale * fraction));
  const labelIndexes = Array.from(new Set([0, Math.floor((days.length - 1) / 2), days.length - 1]));
  return (
    <div className="cur-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="cur-gridline" x1={left} x2={width - right} y1={y(tick)} y2={y(tick)} />
            <text className="cur-axis" x={left - 8} y={y(tick) + 4} textAnchor="end">{formatCount(tick)}</text>
          </g>
        ))}
        {days.map((day, index) => {
          const x = left + slot * index + (slot - barWidth) / 2;
          let base = 0;
          const hover = `${shortDay(day.date)}: ${series.map((item) => `${item.label} ${formatCount(day[item.key])}`).join(', ')}`;
          return (
            <g key={day.date}>
              <title>{hover}</title>
              <rect x={x} y={top} width={barWidth} height={innerHeight} className="cur-bar-hit" />
              {series.map((item) => {
                const value = day[item.key];
                const rect = (
                  <rect key={item.key} x={x} y={y(base + value)} width={barWidth}
                    height={Math.max(0, y(base) - y(base + value))} className={`cur-stack-${item.tone}`} />
                );
                base += value;
                return rect;
              })}
            </g>
          );
        })}
        {labelIndexes.map((index) => (
          <text key={days[index].date} className="cur-axis" x={left + slot * index + slot / 2} y={height - 6}
            textAnchor="middle">{shortDay(days[index].date)}</text>
        ))}
      </svg>
    </div>
  );
};

/** A tiny trend line for the Workflows list's "Last N days" column. */
export const Sparkline: React.FC<{ counts: number[] }> = ({ counts }) => {
  const max = Math.max(0, ...counts);
  if (!max || counts.length < 2) return null;
  const points = counts.map((count, index) => `${((index / (counts.length - 1)) * 78 + 1).toFixed(1)},${(19 - (count / max) * 17).toFixed(1)}`);
  return (
    <svg className="cur-spark" viewBox="0 0 80 20" aria-hidden="true" data-testid="sparkline">
      <polyline points={points.join(' ')} />
    </svg>
  );
};
